import { brandApi } from "../lib/axios";
import { AxiosError } from "axios";
import { toast } from "react-hot-toast";
import { getRichTextLimitError } from "@/utils/richText";

export interface EmailAutomationData {
  id?: string;
  brandDetailId: string;
  newAffiliateSignup: boolean;
  newAffiliateSale: boolean;
  welcomeNewAffiliate: boolean;
  newSale: boolean;
  payoutSent: boolean;
  createAffiliateAccountInvite: boolean;
  affiliateApproval: boolean;
  omnisendApiKey?: string;
  omnisendSendWelcomeEmail?: boolean;
  klaviyoApiKey?: string;
  klaviyoSendWelcomeEmail?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  emailTemplates?: EmailTemplateData[];
  hasNonWelcomeAutomationPermission?: boolean; // Permission flag from backend
}

export interface EmailTemplateData {
  id: string;
  brandDetailId: string;
  type: string;
  subject: string;
  body: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface UpdateEmailAutomationPayload {
  newAffiliateSignup?: boolean;
  newAffiliateSale?: boolean;
  welcomeNewAffiliate?: boolean;
  newSale?: boolean;
  payoutSent?: boolean;
  createAffiliateAccountInvite?: boolean;
  affiliateApproval?: boolean;
  omnisendApiKey?: string;
  omnisendSendWelcomeEmail?: boolean;
  klaviyoApiKey?: string;
  klaviyoSendWelcomeEmail?: boolean;
}

export interface UpdateEmailTemplatePayload {
  subject: string;
  body: string;
}

export class EmailAutomationService {
  private readonly baseUrl = "/email-automation";

  /**
   * Get email automation configuration for current brand
   */
  async getEmailAutomation(): Promise<EmailAutomationData> {
    try {
      const response = await brandApi.get(this.baseUrl);
      return response.data;
    } catch (error) {
      console.error("Error fetching email automation:", error);
      if ((error as AxiosError)?.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        // Handle reauthorization if needed
      }
      throw error;
    }
  }

  /**
   * Update email automation configuration
   */
  async updateEmailAutomation(data: UpdateEmailAutomationPayload): Promise<EmailAutomationData> {
    try {
      const response = await brandApi.put(this.baseUrl, data);
      toast.success("Email automation settings updated successfully");
      return response.data;
    } catch (error) {
      console.error("Error updating email automation:", error);
      toast.error("Failed to update email automation settings");
      throw error;
    }
  }

  /**
   * Get specific email template
   */
  async getEmailTemplate(type: string): Promise<EmailTemplateData> {
    try {
      const response = await brandApi.get(`${this.baseUrl}/templates/${type}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching email template ${type}:`, error);
      throw error;
    }
  }

  /**
   * Update specific email template
   */
  async updateEmailTemplate(type: string, data: UpdateEmailTemplatePayload): Promise<EmailTemplateData> {
    try {
      const response = await brandApi.put(`${this.baseUrl}/templates/${type}`, data);
      toast.success(`${type} email template updated successfully`);
      return response.data;
    } catch (error) {
      console.error(`Error updating email template ${type}:`, error);
      if ((error as AxiosError)?.response?.status === 400) {
        const message = (error as any).response?.data?.message || "Invalid template data";
        toast.error(message);
      } else {
        toast.error(`Failed to update ${type} email template`);
      }
      throw error;
    }
  }

  /**
   * Validate email template data
   */
  validateEmailTemplate(data: UpdateEmailTemplatePayload): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data.subject || data.subject.trim().length === 0) {
      errors.push("Subject is required");
    } else if (data.subject.length > 500) {
      errors.push("Subject cannot exceed 500 characters");
    }

    if (!data.body || data.body.trim().length === 0) {
      errors.push("Email body is required");
    } else {
      const limitError = getRichTextLimitError(data.body, 3000, "email");
      if (limitError) {
        errors.push(limitError);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}

export const emailAutomationService = new EmailAutomationService();
